/**
 * Staff Management Page
 * Allows Company administrators to view and invite Manager/Staff users
 */

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { AssignManagerModal, InviteUserModal, ManageUsersModal, EditManagerModal, EditUserModal } from '../components/modals';
import { PageHeader, Toast } from '../components/common';
import { useToast } from '../hooks/useToast';
import { QuickActionButton } from '../components/dashboard';
import { useAuth } from '../hooks/useAuth';
import { getCompanyUsers } from '../services/user.service';
import { db, functions } from '../config/firebase';
import { convertDocumentTimestamps } from '@shared/utils/firestore';
import type { UserProfile, Company } from '../../../shared/types';

export function StaffManagement() {
  const { currentUser } = useAuth();
  const { toasts, showToast, dismissToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [editingManager, setEditingManager] = useState<UserProfile | null>(null);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [assigningStaff, setAssigningStaff] = useState<UserProfile | null>(null);
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper to find which manager a staff member is assigned to
  const getStaffManager = (staffId: string): UserProfile | undefined => {
    return staff.find(user =>
      user.userType === 'manager' &&
      user.managedStaffIds?.includes(staffId)
    );
  };

  const loadData = async () => {
    if (!currentUser?.companyId) return;

    try {
      setLoading(true);

      // Load company data
      const companyDoc = await getDoc(doc(db, 'Companies', currentUser.companyId));
      if (companyDoc.exists()) {
        setCompany({
          id: companyDoc.id,
          ...convertDocumentTimestamps<Company>(companyDoc.data()),
        } as Company);
      }

      // Load staff
      const companyStaff = await getCompanyUsers(currentUser.companyId);
      setStaff(companyStaff);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentUser?.companyId]);

  const handleInviteSuccess = () => {
    loadData(); // Reload company and staff data
  };

  const handleSendPasswordReset = async (email: string) => {
    try {
      const sendReset = httpsCallable(functions, 'sendPasswordResetEmail');
      await sendReset({ email });
      showToast(`Password reset email sent to ${email}`, 'success');
    } catch {
      showToast('Failed to send reset email. Please try again.', 'error');
    }
  };

  const activeUserCount = staff.length;
  const isAtUserLimit = company ? activeUserCount >= company.maxUsers : false;

  return (
    <div style={{ padding: '32px' }}>
      <PageHeader
        title="Staff Management"
        subtitle="Manage your team members, invite new users, and control access"
        actions={
          <>
            <QuickActionButton
              label="Manage Users"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
              onClick={() => setShowManageUsers(true)}
              disabled={!company}
              variant="secondary"
              style={{ whiteSpace: 'nowrap', width: '160px', justifyContent: 'center' }}
            />
            <QuickActionButton
              label="Invite User"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
              onClick={() => setIsModalOpen(true)}
              disabled={!company || isAtUserLimit}
              variant="primary"
              style={{ whiteSpace: 'nowrap', width: '160px', justifyContent: 'center' }}
            />
          </>
        }
      />

      {company && isAtUserLimit && (
        <div style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>⚠️</span>
          <span style={{ color: '#991b1b', fontSize: '14px', fontWeight: '500' }}>
            User limit reached ({activeUserCount}/{company.maxUsers}). Delete existing users to invite new ones.
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Loading staff...
        </div>
      ) : staff.length === 0 ? (
        <div
          style={{
            backgroundColor: '#f9fafb',
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            padding: '48px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
            No staff members yet
          </h3>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
            Get started by inviting your first manager or staff member.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Invite User
          </button>
        </div>
      ) : (
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Name
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Email
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Phone
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Role
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Assigned Manager
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Status
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {staff.map((member) => (
                <tr key={member.uid} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#111827' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          minWidth: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          flexShrink: 0,
                        }}
                      >
                        {member.firstName?.[0]?.toUpperCase()}{member.lastName?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600' }}>
                          {member.firstName} {member.lastName}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
                    {member.email}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
                    {member.phoneNumber ? `${member.countryCode || ''} ${member.phoneNumber}`.trim() : '-'}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '80px',
                        textAlign: 'center',
                        padding: '4px 0',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: member.userType === 'manager' ? '#dbeafe' : '#e0e7ff',
                        color: member.userType === 'manager' ? '#1e40af' : '#3730a3',
                      }}
                    >
                      {member.userType === 'manager' ? 'Manager' : 'Staff'}
                    </span>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
                    {member.userType === 'staff' ? (
                      (() => {
                        const manager = getStaffManager(member.uid);
                        return manager ? (
                          <span style={{ color: '#111827' }}>
                            {manager.firstName} {manager.lastName}
                          </span>
                        ) : (
                          <span style={{ color: '#ef4444', fontStyle: 'italic' }}>
                            Unassigned
                          </span>
                        );
                      })()
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px' }}>
                    {member.requirePasswordChange ? (
                      <span
                        style={{
                          display: 'inline-block',
                          width: '130px',
                          textAlign: 'center',
                          padding: '4px 0',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                        }}
                      >
                        Pending First Login
                      </span>
                    ) : (
                      <span
                        style={{
                          display: 'inline-block',
                          width: '130px',
                          textAlign: 'center',
                          padding: '4px 0',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: '#d1fae5',
                          color: '#065f46',
                        }}
                      >
                        Active
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => setEditingUser(member)}
                        style={{
                          width: '64px',
                          padding: '5px 0',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#374151',
                          backgroundColor: '#ffffff',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                        }}
                      >
                        Edit
                      </button>
                      {member.userType === 'manager' ? (
                        <button
                          onClick={() => setEditingManager(member)}
                          style={{
                            width: '130px',
                            padding: '5px 0',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#1d4ed8',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                        >
                          Manage Staff
                        </button>
                      ) : (
                        <button
                          onClick={() => setAssigningStaff(member)}
                          style={{
                            width: '130px',
                            padding: '5px 0',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: getStaffManager(member.uid) ? '#1d4ed8' : '#b91c1c',
                            backgroundColor: getStaffManager(member.uid) ? '#eff6ff' : '#fef2f2',
                            border: `1px solid ${getStaffManager(member.uid) ? '#bfdbfe' : '#fecaca'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                        >
                          {getStaffManager(member.uid) ? 'Edit Manager' : 'Assign Manager'}
                        </button>
                      )}
                      <button
                        onClick={() => member.email && handleSendPasswordReset(member.email)}
                        style={{
                          padding: '5px 10px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#6b7280',
                          backgroundColor: '#ffffff',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                        title={`Send password reset to ${member.email}`}
                      >
                        Reset Password
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {company && (
        <>
          <InviteUserModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            company={company}
            currentUserCount={activeUserCount}
            companyAdminEmail={currentUser?.email || ''}
            companyAdminName={`${currentUser?.firstName} ${currentUser?.lastName}`}
            onSuccess={handleInviteSuccess}
          />
          <ManageUsersModal
            isOpen={showManageUsers}
            onClose={() => setShowManageUsers(false)}
            company={company}
            onSuccess={handleInviteSuccess}
          />
          {editingManager && (
            <EditManagerModal
              isOpen={!!editingManager}
              onClose={() => setEditingManager(null)}
              manager={editingManager}
              companyId={company.id}
              onSuccess={handleInviteSuccess}
            />
          )}
          {editingUser && (
            <EditUserModal
              isOpen={!!editingUser}
              onClose={() => setEditingUser(null)}
              user={editingUser}
              companyId={company.id}
              canEditEmail={true}
              onSuccess={() => { setEditingUser(null); handleInviteSuccess(); }}
            />
          )}
          {assigningStaff && (
            <AssignManagerModal
              isOpen={!!assigningStaff}
              onClose={() => setAssigningStaff(null)}
              staffMember={assigningStaff}
              companyId={company.id}
              currentManager={getStaffManager(assigningStaff.uid)}
              onSuccess={handleInviteSuccess}
            />
          )}
        </>
      )}
      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
